import { Util } from '@antv/g6';
import { DrawOpts, NodeModel } from '../../../../types';
import { shapeNames, groupIds, ButtonType } from '../../../constants';
import { getAbbreviatedNumber, getTextWidth, getChild, addTooltipToText } from '../../../utils';
import { colors } from 'ui/theme';
import { pluralizeWord } from 'modules/campaigns/lib/utils';

const DEFAULT_ICON_WIDTH = 16;
const DEFAULT_LINE_HEIGHT = 16;

enum TotalUserType {
  ENTERED = 'entered',
  EXITED = 'exited',
  IN_PROGRESS = 'in tile',
}

export const shouldNumUsersGroupShow = (
  numUsers: string | number,
  totalUserType: TotalUserType,
) => {
  try {
    const num = typeof numUsers === 'string' ? parseInt(numUsers, 10) : numUsers;

    switch (totalUserType) {
      case TotalUserType.ENTERED:
      case TotalUserType.EXITED:
        return num >= 0;
      case TotalUserType.IN_PROGRESS:
        return num > 0;
      default:
        return num > 0;
    }
  } catch {
    return false;
  }
};

const getModelKey = (totalUserType: TotalUserType) => {
  switch (totalUserType) {
    case TotalUserType.ENTERED:
      return 'visitorsEnteredCount';
    case TotalUserType.EXITED:
      return 'visitorsExitedCount';
    case TotalUserType.IN_PROGRESS:
      return 'numUsers';
    default:
      return '';
  }
};

const getButtonType = (totalUserType: TotalUserType) => {
  switch (totalUserType) {
    case TotalUserType.ENTERED:
      return ButtonType.NumUsersEntered;
    case TotalUserType.EXITED:
      return ButtonType.NumUsersExited;
    case TotalUserType.IN_PROGRESS:
      return ButtonType.NumUsers;
    default:
      return ButtonType.NumUsers;
  }
};

const getGroupId = (totalUserType: TotalUserType) => {
  switch (totalUserType) {
    case TotalUserType.ENTERED:
      return groupIds.numUsersEntered;
    case TotalUserType.EXITED:
      return groupIds.numUsersExited;
    case TotalUserType.IN_PROGRESS:
      return groupIds.numUsers;
    default:
      return '';
  }
};

const shouldBeVisible = (totalUserType: TotalUserType, key: string, model: DrawOpts['model']) => {
  const isVisitorCountsOn = !!model?.showVisitorCounts;
  const isNumUsersOn = !!model?.showNumUsersCount;
  const numUsersCount = model?.[key];

  switch (totalUserType) {
    case TotalUserType.ENTERED:
      return !!isVisitorCountsOn && shouldNumUsersGroupShow(numUsersCount, totalUserType);
    case TotalUserType.EXITED:
      return !!isVisitorCountsOn && shouldNumUsersGroupShow(numUsersCount, totalUserType);
    case TotalUserType.IN_PROGRESS: {
      return isNumUsersOn && shouldNumUsersGroupShow(numUsersCount, totalUserType);
    }
    default: {
      return false;
    }
  }
};

const shouldBeDisabled = (totalUserType: TotalUserType, key: string, model: DrawOpts['model']) => {
  const numUsersCount = model?.[key];
  const isDisabled = numUsersCount === 0;

  switch (totalUserType) {
    case TotalUserType.ENTERED:
      return isDisabled;
    case TotalUserType.EXITED:
      return isDisabled;
    case TotalUserType.IN_PROGRESS: {
      return false;
    }
    default: {
      return false;
    }
  }
};

const getNumUserText = (model: NodeModel, key: string) => model?.[key] || 0;

const getShapesByTotalType = (totalUserType: TotalUserType) => {
  switch (totalUserType) {
    case TotalUserType.ENTERED:
      return {
        numUsersGroup: shapeNames.numUsersEnteredGroup,
        numUsersIcon: shapeNames.numUsersEnteredIcon,
        numUsersWrapper: shapeNames.numUsersEnteredWrapper,
        numUsersPrimaryText: shapeNames.numUsersEnteredPrimaryText,
        numUsersSecondaryText: shapeNames.numUsersEnteredSecondaryText,
      };
    case TotalUserType.EXITED:
      return {
        numUsersGroup: shapeNames.numUsersExitedGroup,
        numUsersIcon: shapeNames.numUsersExitedIcon,
        numUsersWrapper: shapeNames.numUsersExitedWrapper,
        numUsersPrimaryText: shapeNames.numUsersExitedPrimaryText,
        numUsersSecondaryText: shapeNames.numUsersExitedSecondaryText,
      };
    case TotalUserType.IN_PROGRESS:
      return {
        numUsersGroup: shapeNames.numUsersGroup,
        numUsersIcon: shapeNames.numUsersIcon,
        numUsersWrapper: shapeNames.numUsersWrapper,
        numUsersPrimaryText: shapeNames.numUsersPrimaryText,
        numUsersSecondaryText: shapeNames.numUsersSecondaryText,
      };
    default:
      return {
        numUsersGroup: shapeNames.numUsersGroup,
        numUsersIcon: shapeNames.numUsersIcon,
        numUsersWrapper: shapeNames.numUsersWrapper,
        numUsersPrimaryText: shapeNames.numUsersPrimaryText,
        numUsersSecondaryText: shapeNames.numUsersSecondaryText,
      };
  }
};

const getNumUserOptions = (
  totalUserType: TotalUserType,
  options: DrawOpts['options'],
  isDisabled = false,
) => {
  const shapeNamesByTotalType = getShapesByTotalType(totalUserType);
  return {
    ...options,
    numUsersIconStyle: {
      ...(options[shapeNamesByTotalType.numUsersIcon] || {}),
      opacity: isDisabled ? 0.6 : 1,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    },
    numUsersWrapperStyle: {
      ...(options[shapeNamesByTotalType.numUsersWrapper] || {}),
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    },
    numUsersPrimaryTextStyle: {
      ...(options[shapeNamesByTotalType.numUsersPrimaryText] || {}),
      fill: isDisabled ? colors.textDisabled : colors.textPrimary,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    },
    numUsersSecondaryTextStyle: {
      ...(options[shapeNamesByTotalType.numUsersSecondaryText] || {}),
      fill: isDisabled ? colors.textDisabled : colors.textSecondary,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    },
  };
};

const getNumUserPositionDetails = (
  totalUserType: TotalUserType,
  { size, options }: Pick<DrawOpts, 'size' | 'options'>,
) => {
  const [width] = size || [0];
  const {
    numUsersWrapperStyle = {},
    numUsersPrimaryTextStyle = {},
    numUsersIconStyle = {},
  } = getNumUserOptions(totalUserType, options);
  const { y = 0, lineWidth: borderWidth = 1 } = numUsersWrapperStyle;
  const { lineHeight } = numUsersPrimaryTextStyle;
  const { iconWidth = DEFAULT_ICON_WIDTH } = numUsersIconStyle;

  const [wrapperPaddingTop, wrapperPaddingRight] = Util.formatPadding(numUsersWrapperStyle.padding);
  const [iconPaddingTop, iconPaddingRight, , iconPaddingLeft] = Util.formatPadding(
    numUsersIconStyle.padding,
  );
  const [textPaddingTop, , , ,] = Util.formatPadding(numUsersPrimaryTextStyle.padding);
  let shift = totalUserType === TotalUserType.IN_PROGRESS ? 0 : 30;
  return {
    iconX: width + shift - iconWidth + (iconPaddingLeft - iconPaddingRight),
    iconY: y + wrapperPaddingTop + iconPaddingTop,
    textX: width + shift,
    textY: y + wrapperPaddingTop + textPaddingTop + lineHeight - borderWidth,
    wrapperX: width + shift - iconWidth - wrapperPaddingRight,
  };
};

const getNumDetails = (
  totalUserType: TotalUserType,
  key: string,
  { size, options, model }: Omit<DrawOpts, 'group'>,
) => {
  const isSelected = !!model?.isSelected;
  const { iconX, iconY, textX, textY, wrapperX } = getNumUserPositionDetails(totalUserType, {
    size,
    options,
  });
  const {
    numUsersWrapperStyle = {},
    numUsersPrimaryTextStyle = {},
    numUsersIconStyle = {},
  } = getNumUserOptions(totalUserType, options);

  const { lineWidth: borderWidth = 1 } = numUsersWrapperStyle;
  const { lineHeight = DEFAULT_LINE_HEIGHT } = numUsersPrimaryTextStyle;
  const { iconWidth = DEFAULT_ICON_WIDTH } = numUsersIconStyle;

  const [wrapperPaddingTop, wrapperPaddingRight, wrapperPaddingBottom, wrapperPaddingLeft] =
    Util.formatPadding(numUsersWrapperStyle.padding);
  const parseText = getNumUserText(model as NodeModel, key);
  const getAbbreviatedText = getAbbreviatedNumber(parseText);
  const text = isSelected ? new Intl.NumberFormat().format(parseText) : getAbbreviatedText;
  const textWidth = getTextWidth(text, numUsersPrimaryTextStyle);
  const totalTextWidth = isSelected
    ? getTextWidth(`${text} ${totalUserType}`, numUsersPrimaryTextStyle)
    : textWidth;
  const wrapperWidth = wrapperPaddingRight + wrapperPaddingLeft + iconWidth + totalTextWidth;
  const wrapperHeight = wrapperPaddingTop + wrapperPaddingBottom + borderWidth * 2 + lineHeight;

  return {
    iconX,
    iconY,
    text,
    textWidth,
    textX,
    textY,
    secondaryTextX: textWidth + textX + 2,
    secondaryTextY: textY,
    wrapperHeight,
    wrapperRadius: wrapperHeight / 2,
    wrapperWidth,
    wrapperX,
  };
};

const getTooltipText = (totalUserType: TotalUserType, model: NodeModel) => {
  switch (totalUserType) {
    case TotalUserType.ENTERED:
      return `${model?.visitorsEnteredCount} ${pluralizeWord('user', model?.visitorsEnteredCount, 'users')} have entered this tile since \n journey event tracking was turned on.`;
    case TotalUserType.EXITED:
      return `${model?.visitorsExitedCount} ${pluralizeWord('user', model?.visitorsEnteredCount, 'users')} have exited this tile since \n journey event tracking was turned on.`;
    case TotalUserType.IN_PROGRESS:
      return `${model?.numUsers} ${pluralizeWord('user', model?.visitorsEnteredCount, 'users')} are currently in this tile.`;
    default:
      return '';
  }
};

export const drawNumUsers = (
  totalUserType: TotalUserType,
  { size, options, group, model }: DrawOpts,
) => {
  const isSelected = !!model?.isSelected;
  const modelKey = getModelKey(totalUserType);
  const groupId = getGroupId(totalUserType);
  const buttonType = getButtonType(totalUserType);
  const commonProps = { buttonType };
  const shapesByTotalType = getShapesByTotalType(totalUserType);
  const beVisible = shouldBeVisible(totalUserType, modelKey, model);
  const beDisabled = shouldBeDisabled(totalUserType, modelKey, model);

  const totalNumUsersGroup = group?.addGroup({
    ...commonProps,
    id: groupId,
    visible: beVisible,
    name: shapesByTotalType.numUsersGroup,
    className: shapesByTotalType.numUsersGroup,
    draggable: true,
    capture: true,
  });

  const {
    numUsersWrapperStyle,
    numUsersIconStyle,
    numUsersSecondaryTextStyle,
    numUsersPrimaryTextStyle,
  } = getNumUserOptions(totalUserType, options, beDisabled);
  const {
    iconX,
    iconY,
    text,
    textX,
    textY,
    secondaryTextX,
    secondaryTextY,
    wrapperHeight,
    wrapperRadius,
    wrapperWidth,
    wrapperX,
  } = getNumDetails(totalUserType, modelKey, { size, options, model });

  const wrapper = totalNumUsersGroup?.addShape('rect', {
    ...commonProps,
    name: shapesByTotalType.numUsersWrapper,
    className: shapesByTotalType.numUsersWrapper,
    draggable: true,
    attrs: {
      x: wrapperX,
      width: wrapperWidth,
      height: wrapperHeight,
      radius: wrapperRadius,
      ...numUsersWrapperStyle,
    },
  });

  totalNumUsersGroup?.addShape('text', {
    ...commonProps,
    name: shapesByTotalType.numUsersPrimaryText,
    className: shapesByTotalType.numUsersPrimaryText,
    draggable: true,
    attrs: {
      text,
      x: textX,
      y: textY,
      ...numUsersPrimaryTextStyle,
    },
  });

  totalNumUsersGroup?.addShape('text', {
    ...commonProps,
    name: shapesByTotalType.numUsersSecondaryText,
    className: shapesByTotalType.numUsersSecondaryText,
    visible: isSelected,
    draggable: true,
    attrs: {
      text: totalUserType,
      x: secondaryTextX,
      y: secondaryTextY,
      ...numUsersSecondaryTextStyle,
    },
  });

  totalNumUsersGroup?.addShape('image', {
    ...commonProps,
    name: shapesByTotalType.numUsersIcon,
    className: shapesByTotalType.numUsersIcon,
    draggable: true,
    attrs: {
      x: iconX,
      y: iconY,
      ...numUsersIconStyle,
    },
  });

  wrapper?.toBack();

  return totalNumUsersGroup;
};

export const updateNumUsers = (
  totalUserType: TotalUserType,
  { size, group, model, options }: DrawOpts,
) => {
  const beVisible = shouldBeVisible(totalUserType, getModelKey(totalUserType), model);
  const beDisabled = shouldBeDisabled(totalUserType, getModelKey(totalUserType), model);
  const groupId = getGroupId(totalUserType);

  const numTotalUserGroup = getChild(group, groupId);

  if (!numTotalUserGroup) return;

  const isVisible = numTotalUserGroup?.get('visible');

  // Don't bother with the rest of the calculations, as it is hidden anyway
  if (!beVisible) {
    if (isVisible) {
      numTotalUserGroup?.hide();
    }
    return;
  } else if (!isVisible) {
    numTotalUserGroup?.show();
  }

  const modelKey = getModelKey(totalUserType);
  const shapesByTotalType = getShapesByTotalType(totalUserType);

  const {
    numUsersWrapperStyle,
    numUsersIconStyle,
    numUsersSecondaryTextStyle,
    numUsersPrimaryTextStyle,
  } = getNumUserOptions(totalUserType, options, beDisabled);

  const {
    iconX,
    iconY,
    text,
    textX,
    textY,
    secondaryTextX,
    secondaryTextY,
    wrapperHeight,
    wrapperRadius,
    wrapperWidth,
    wrapperX,
  } = getNumDetails(totalUserType, modelKey, { size, options, model });

  const wrapper = getChild(numTotalUserGroup, shapesByTotalType.numUsersWrapper);

  wrapper?.attr({
    x: wrapperX,
    width: wrapperWidth,
    height: wrapperHeight,
    radius: wrapperRadius,
    cursor: numUsersWrapperStyle.cursor,
  });

  const numUsersText = numTotalUserGroup?.findByClassName(shapesByTotalType.numUsersPrimaryText);

  numUsersText?.attr({
    text,
    x: textX,
    y: textY,
    fill: numUsersPrimaryTextStyle.fill,
    cursor: numUsersPrimaryTextStyle.cursor,
  });

  const tooltipText = getTooltipText(totalUserType, model as NodeModel);

  addTooltipToText({
    shape: numUsersText,
    group,
    tooltipText: tooltipText,
    maxWidth: text.length,
    isUnderlined: false,
  });

  const icon = numTotalUserGroup?.findByClassName(shapesByTotalType.numUsersIcon);

  icon?.attr({
    x: iconX,
    y: iconY,
    opacity: numUsersIconStyle.opacity,
    cursor: numUsersIconStyle.cursor,
  });

  const secondaryText = numTotalUserGroup?.findByClassName(shapesByTotalType.numUsersSecondaryText);
  if (model.isSelected) {
    secondaryText?.attr({
      totalUserType,
      x: secondaryTextX,
      y: secondaryTextY,
      fill: numUsersSecondaryTextStyle.fill,
      cursor: numUsersSecondaryTextStyle.cursor,
    });
    secondaryText?.show();
  } else {
    secondaryText?.hide();
  }
};

export const updateNumUsersPosition = (
  totalUserType: TotalUserType,
  { size, group, options }: Omit<DrawOpts, 'model'>,
) => {
  const { numUsersWrapper, numUsersPrimaryText, numUsersIcon } =
    getShapesByTotalType(totalUserType);

  const { iconX, iconY, textX, textY, wrapperX } = getNumUserPositionDetails(totalUserType, {
    size,
    options,
  });
  const wrapper = getChild(group, numUsersWrapper);

  // Position hasn't changed, so exit early to avoid unnessary changes
  if (wrapper?.attrs?.x === wrapperX) return;

  wrapper?.attr({ x: wrapperX });
  group?.findByClassName(numUsersPrimaryText)?.attr({ x: textX, y: textY });
  group?.findByClassName(numUsersIcon)?.attr({ x: iconX, y: iconY });
};

export default {
  draw: drawNumUsers,
  update: updateNumUsers,
  updatePosition: updateNumUsersPosition,
  shouldBeVisible: shouldNumUsersGroupShow,
  TotalUserType,
};
